/**
 * OMON-348 — what "Save changes" will do to someone's Circle of Support access.
 *
 * Circle of Support access belongs to a RELATIONSHIP (this person × this
 * client), so the dialog shows one tick box per client they are family of.
 * Ticked = they may use the app for that client. This module turns the ticks
 * into the grant / revoke steps Save applies, and into the "Will: …" line
 * shown before Save — the same shape as environment-plan.ts, because the
 * dialog has ONE Save (Ed, 2026-09-18) and both halves must read alike.
 *
 * Pure, so vitest can exercise it.
 */
import type { FamilyLink } from "./user-catalogue";

export interface FamilyStep {
  kind: "grant" | "revoke";
  relatedPersonDocId: string;
  clientName: string;
}

/** A link HAS access when the office granted it or the person is signed in. */
export function linkHasAccess(link: FamilyLink): boolean {
  return link.accessState === "using" || link.accessState === "granted";
}

/** The steps that turn the current state into the ticked state. Unchanged
 *  links produce nothing, so re-saving an untouched dialog is a no-op. */
export function planFamilyChanges(links: FamilyLink[], ticked: Set<string>): FamilyStep[] {
  const steps: FamilyStep[] = [];
  for (const link of links) {
    const want = ticked.has(link.relatedPersonDocId);
    const has = linkHasAccess(link);
    if (want === has) continue;
    steps.push({
      kind: want ? "grant" : "revoke",
      relatedPersonDocId: link.relatedPersonDocId,
      clientName: link.clientName ?? "a client",
    });
  }
  return steps;
}

export function describeFamilyStep(step: FamilyStep): string {
  return step.kind === "grant"
    ? `Give Circle of Support access for ${step.clientName}`
    : `Remove Circle of Support access for ${step.clientName}`;
}

// ── ADR-0046 amendment 2026-09-21, step (c): one email box per person ────
//
// Ed, 2026-09-22: ONE email box for the person, applied to every contact
// record of theirs. Setting it takes their contact details over from the
// OneTouch sync (server: related_person_action `set-email`).

export interface EmailStep {
  kind: "set-email";
  relatedPersonDocIds: string[];
  email: string;
  /** The different addresses being replaced, when there were several. */
  replacing: string[];
}

/** Same rule as the server's normalise_email: one @, no spaces, a dot in
 *  the domain; trimmed and lower-cased (the catalogue's join key). */
export function normaliseEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? email : null;
}

/** The distinct emails currently on this person's contact records. */
export function currentEmails(links: FamilyLink[]): string[] {
  return Array.from(new Set(links.map((l) => l.email).filter((e): e is string => !!e))).sort();
}

/** ☠️ A row is one EMAIL, and some addresses belong to several different
 *  people (a couple sharing an inbox). One box would change it for all of
 *  them, so such rows get no email box. */
export function isSharedInbox(links: FamilyLink[]): boolean {
  return new Set(links.map((l) => l.name).filter((n) => !!n)).size > 1;
}

export type EmailPlan =
  | { kind: "none" }
  | { kind: "invalid"; message: string }
  | { kind: "step"; step: EmailStep };

/** What the email box will do on Save. Blank or unchanged → nothing (the
 *  box cannot delete an address, only set one). */
export function planEmailChange(links: FamilyLink[], typed: string): EmailPlan {
  if (links.length === 0 || typed.trim() === "") return { kind: "none" };
  const email = normaliseEmail(typed);
  if (email === null) {
    return { kind: "invalid", message: "That doesn't look like an email address." };
  }
  if (links.every((l) => l.email === email)) return { kind: "none" };
  const current = currentEmails(links);
  return {
    kind: "step",
    step: {
      kind: "set-email",
      relatedPersonDocIds: links.map((l) => l.relatedPersonDocId),
      email,
      replacing: current.length > 1 ? current : [],
    },
  };
}

export function describeEmailStep(step: EmailStep): string {
  const where = step.relatedPersonDocIds.length > 1
    ? ` for all ${step.relatedPersonDocIds.length} clients` : "";
  const replacing = step.replacing.length > 0
    ? ` (replacing ${step.replacing.length} different addresses: ${step.replacing.join(", ")})` : "";
  return `Set their email to ${step.email}${where}${replacing}; OneTouch will no longer change it`;
}
